import axios from "axios";

const PAYPAL_LOG_URL = "/api/log/paypal/error";

/**
 * Busca debug_id en un objeto de forma recursiva (objeto o array).
 * @param {object|array} obj
 * @returns {string|undefined}
 */
function findDebugId(obj) {
    if (obj == null) return undefined;
    if (typeof obj === "string") return undefined;
    if (typeof obj.debug_id === "string" && obj.debug_id) return obj.debug_id;
    if (Array.isArray(obj)) {
        for (const item of obj) {
            const found = findDebugId(item);
            if (found) return found;
        }
        return undefined;
    }
    if (typeof obj === "object") {
        for (const key of ["details", "error", "response", "body", "data"]) {
            const found = findDebugId(obj[key]);
            if (found) return found;
        }
        for (const value of Object.values(obj)) {
            const found = findDebugId(value);
            if (found) return found;
        }
    }
    return undefined;
}

/**
 * Intenta parsear un string como JSON y extraer debug_id si existe.
 * @param {string} str
 * @returns {{ parsed: object|array|null, debug_id: string|undefined }}
 */
function tryParseJsonForDebugId(str) {
    if (typeof str !== "string" || !str.trim())
        return { parsed: null, debug_id: undefined };
    const trimmed = str.trim();
    if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
        try {
            const parsed = JSON.parse(str);
            return { parsed, debug_id: findDebugId(parsed) };
        } catch (_) {
            return { parsed: null, debug_id: undefined };
        }
    }
    return { parsed: null, debug_id: undefined };
}

/**
 * Obtiene el header Paypal-Debug-Id (case-insensitive) de un objeto de headers.
 * @param {object} headersObj
 * @returns {string|undefined}
 */
function getPaypalDebugIdFromHeaders(headersObj) {
    if (headersObj == null || typeof headersObj !== "object") return undefined;
    const lower = (s) => (typeof s === "string" ? s.toLowerCase() : "");
    for (const [key, value] of Object.entries(headersObj)) {
        if (lower(key) === "paypal-debug-id" && value != null && String(value).trim()) {
            return String(value).trim();
        }
    }
    return undefined;
}

/**
 * Busca en un string posibles debug_id de PayPal (cadenas alfanuméricas 14-22 chars).
 * Útil para mensajes como "Request to post ... /capture failed".
 * @param {string} str
 * @returns {string|undefined}
 */
function extractPaypalLikeIdFromString(str) {
    if (typeof str !== "string" || !str.trim()) return undefined;
    const PAYPAL_ID_REGEX = /\b([A-Za-z0-9_-]{14,22})\b/g;
    const matches = str.match(PAYPAL_ID_REGEX) || [];
    for (const m of matches) {
        if (m.length >= 14 && m.length <= 22 && !/^\d+$/.test(m)) return m;
    }
    return undefined;
}

/**
 * Serializa un objeto usando todas sus propiedades propias (incl. no enumerables).
 * @param {object} obj
 * @param {WeakSet} seen
 * @returns {object}
 */
function serializeRawObject(obj, seen = new WeakSet()) {
    if (obj == null) return obj;
    if (typeof obj !== "object") return obj;
    if (seen.has(obj)) return "[Circular]";
    const out = { _raw: true, _type: Object.prototype.toString.call(obj) };
    seen.add(obj);
    const keys = Object.getOwnPropertyNames(obj);
    for (const key of keys) {
        try {
            const desc = Object.getOwnPropertyDescriptor(obj, key);
            let value = desc && "value" in desc ? desc.value : obj[key];
            if (value != null && typeof value === "object") {
                value = serializeRawObject(value, seen);
            } else if (typeof value === "function") {
                value = "[Function]";
            }
            out[key] = value;
        } catch (_) {
            out[key] = "[Unserializable]";
        }
    }
    return out;
}

/**
 * Serializa headers de una respuesta (Headers o objeto) a un objeto plano.
 * @param {Headers|object} headers
 * @returns {object}
 */
function serializeHeaders(headers) {
    if (headers == null) return {};
    const out = {};
    try {
        if (typeof headers.forEach === "function") {
            headers.forEach((value, key) => {
                out[key] = value;
            });
        } else if (typeof headers.entries === "function") {
            for (const [key, value] of headers.entries()) {
                out[key] = value;
            }
        } else if (typeof headers === "object") {
            for (const key of Object.keys(headers)) {
                out[key] = headers[key];
            }
        }
    } catch (_) {
        out._serialize_error = "Could not serialize headers";
    }
    return out;
}

/**
 * Serializa un error de PayPal (u otro) a un objeto plano para enviar al backend.
 * - Extrae debug_id explícitamente (del error, de details/message como JSON, o de response.data).
 * - En errores de red (Axios/fetch), captura status, headers y cuerpo de respuesta.
 *
 * @param {Error|object|string} err
 * @returns {object}
 */
function serializeError(err) {
    if (err == null) return { _note: "null or undefined error" };
    if (typeof err === "string") {
        const { parsed, debug_id } = tryParseJsonForDebugId(err);
        const base = { message: err };
        if (debug_id) base.debug_id = debug_id;
        if (parsed) base.parsed_body = parsed;
        const fromStr = extractPaypalLikeIdFromString(err);
        if (fromStr && !base.debug_id) base.debug_id = fromStr;
        return base;
    }

    try {
        // ProgressEvent o error de capa de transporte: serializar objeto completo (incl. no enumerables)
        if (Object.prototype.toString.call(err) === "[object ProgressEvent]") {
            const raw = serializeRawObject(err);
            raw._note = "ProgressEvent at top level (transport/network)";
            let debug_id_candidate = extractPaypalLikeIdFromString(String(err));
            if (!debug_id_candidate && err.target && err.target.responseText) {
                const { debug_id: fromBody } = tryParseJsonForDebugId(err.target.responseText);
                if (fromBody) debug_id_candidate = fromBody;
            }
            if (debug_id_candidate) raw.debug_id_candidate = debug_id_candidate;
            return raw;
        }

        let debug_id = findDebugId(err);

        // Errores de red (Axios): capturar status, response.data y headers
        const isAxiosError =
            err.isAxiosError === true ||
            (err.response !== undefined && err.request !== undefined);
        if (isAxiosError && err.response) {
            const response = err.response;
            const status = response.status;
            const data = response.data;
            const headers = serializeHeaders(response.headers || {});

            if (!debug_id && data) debug_id = findDebugId(data);
            if (typeof data === "string") {
                const { parsed, debug_id: fromStr } =
                    tryParseJsonForDebugId(data);
                if (fromStr) debug_id = fromStr;
            }
            if (!debug_id) debug_id = getPaypalDebugIdFromHeaders(headers);

            const networkPayload = {
                network_error: true,
                status_code: status,
                response_headers: headers,
                response_body: data,
            };
            if (debug_id) networkPayload.debug_id = debug_id;
            return {
                message:
                    err.message || `Request failed with status code ${status}`,
                code: err.code,
                ...networkPayload,
            };
        }

        // Request falló sin response (red, CORS, etc.)
        if (isAxiosError && err.request && !err.response) {
            const fallback = {
                network_error: true,
                no_response: true,
                message: err.message || String(err),
            };
            if (debug_id) fallback.debug_id = debug_id;
            return fallback;
        }

        const seen = new WeakSet();
        const toPlain = (o) => {
            if (o == null) return o;
            if (typeof o !== "object") return o;
            if (seen.has(o)) return "[Circular]";
            if (Object.prototype.toString.call(o) === "[object Error]") {
                seen.add(o);
                const obj = {
                    name: o.name,
                    message: o.message,
                    ...(o.details && { details: toPlain(o.details) }),
                    ...(o.debug_id && { debug_id: o.debug_id }),
                };
                return obj;
            }
            if (
                Object.prototype.toString.call(o) === "[object ProgressEvent]"
            ) {
                const raw = serializeRawObject(o);
                const msg = String(o);
                const fromMsg = extractPaypalLikeIdFromString(msg);
                if (fromMsg) raw.debug_id_candidate = fromMsg;
                return raw;
            }
            if (Array.isArray(o)) return o.map(toPlain);
            seen.add(o);
            const out = {};
            for (const k of Object.keys(o)) {
                try {
                    out[k] = toPlain(o[k]);
                } catch (_) {
                    out[k] = "[Unserializable]";
                }
            }
            return out;
        };

        const serialized = toPlain(err);
        if (
            debug_id &&
            typeof serialized === "object" &&
            serialized !== null &&
            !serialized.debug_id
        ) {
            serialized.debug_id = debug_id;
        }
        // Si message es string tipo JSON, intentar parsear y añadir debug_id si aparece
        if (
            serialized &&
            serialized.message &&
            typeof serialized.message === "string"
        ) {
            const { parsed, debug_id: fromMsg } = tryParseJsonForDebugId(
                serialized.message
            );
            if (fromMsg && !serialized.debug_id) serialized.debug_id = fromMsg;
            if (parsed) serialized.parsed_message = parsed;
            // "Request to post ... /capture failed": buscar ID tipo PayPal en el mensaje
            if (
                !serialized.debug_id &&
                /failed/i.test(serialized.message)
            ) {
                const fromFailed = extractPaypalLikeIdFromString(
                    serialized.message
                );
                if (fromFailed) serialized.debug_id_candidate = fromFailed;
            }
        }
        return serialized;
    } catch (_) {
        const msg = String(err);
        const out = { message: msg, _serialize_fallback: true };
        if (msg === "[object ProgressEvent]" && err != null && typeof err === "object") {
            Object.assign(out, serializeRawObject(err));
        } else {
            const fromStr = extractPaypalLikeIdFromString(msg);
            if (fromStr) out.debug_id_candidate = fromStr;
        }
        return out;
    }
}

/**
 * Composable para registrar errores de PayPal en el backend (paypalLog.log).
 *
 * @returns {{ logPaypalError: function }}
 */
export function usePaypalLogger() {
    /**
     * Envía el error al endpoint de registro.
     *
     * @param {Error|object|string} error - Objeto o mensaje de error de PayPal
     * @param {object} options
     * @param {number|string|null} [options.ordenId] - ID de la orden
     * @param {number|string|null} [options.maniobraId] - ID de la maniobra
     * @param {string} options.component - 'PagoEnvio' | 'GestorPagos'
     * @param {string} [options.message] - Mensaje corto (ej. "Capture no COMPLETED")
     * @param {string} [options.paypalClientId] - Client ID usado, si está disponible en el error
     */
    const logPaypalError = (error, options = {}) => {
        const {
            ordenId = null,
            maniobraId = null,
            component,
            message = null,
            paypalClientId = null,
        } = options;

        const errorDetails = serializeError(error);
        const context = {};
        if (message) context.message = message;
        if (paypalClientId) context.paypal_client_id = paypalClientId;

        axios
            .post(PAYPAL_LOG_URL, {
                orden_id: ordenId,
                maniobra_id: maniobraId,
                component,
                error_details: errorDetails,
                context: Object.keys(context).length ? context : undefined,
            })
            .catch((reqErr) => {
                console.error(
                    "No se pudo enviar el log de PayPal al servidor:",
                    reqErr
                );
            });
    };

    return { logPaypalError };
}
